/**
 * "Create Order from Quote" on the customer 360 Quotes tab (kanban 19046830).
 *
 * Turns the profile the page already holds into the pre-selection the
 * CreateOrderDialog takes, so the dialog opens with the customer picked and
 * the quote linked (its lines and prices are copied into the draft by the
 * manual-create endpoint when `sales_quote_id` is set).
 */
import type { CreateOrderPrefill, CustomerSearchResult } from '../../../components/portal/CreateOrderDialog';
import type { PortalQuoteSummary } from '../../../services/portalOrders';
import type { Customer360Profile, CustomerQuoteItem } from '../../../types/customer';

/** The customer typeahead row the dialog would have produced for this profile. */
export function customerFromProfile(profile: Customer360Profile): CustomerSearchResult {
  const { contact_info, salesperson } = profile;
  return {
    id: contact_info.id,
    sage_customer_no: contact_info.sage_customer_no,
    customer_name: contact_info.customer_name,
    city: contact_info.city,
    state: contact_info.state,
    salesperson_no: salesperson?.salesperson_no ?? null,
    salesperson_name: salesperson?.salesperson_name ?? null,
  };
}

/**
 * The picker row for a quote off the profile. The 360 row allows null status
 * and created date where the picker row does not; the picker renders the
 * date's first ten characters, so an unknown date renders as nothing.
 */
export function quoteSummaryFromProfileQuote(quote: CustomerQuoteItem): PortalQuoteSummary {
  return {
    id: quote.id,
    source_id: quote.source_id,
    project_name: quote.project_name,
    status: quote.status ?? 'unknown',
    created_date: quote.created_date ?? '',
    expiration_date: quote.expiration_date,
    product_count: quote.product_count,
    estimated_total: quote.estimated_total,
  };
}

export function buildCreateOrderPrefill(
  profile: Customer360Profile,
  quote: CustomerQuoteItem,
): CreateOrderPrefill {
  return {
    customer: customerFromProfile(profile),
    quote: quoteSummaryFromProfileQuote(quote),
  };
}

/** Where the portal edits a freshly created draft: the orders page deep link. */
export function createdOrderUrl(orderId: string): string {
  return `/portal/orders?orderId=${encodeURIComponent(orderId)}`;
}
